import cashflowViewConsts from '../../../constants/cashflow-view';
import ChangeTransactionCategorySettingsPopup
  // eslint-disable-next-line max-len
  from '../../../pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/current-month/components/cashflow-edit/edit-transaction/ChangeTransactionCategorySettingsPopup.vue';

const ENRICHMENT_INPUT_TYPES = {
  TRANSACTION_ID: 'transactionId',
  BUSINESS_NAME: 'businessName',
};

function normalizeTrackingCategories(trackingCategories) {
  return _.map(trackingCategories, category => {
    return {
      categoryId: category.categoryId,
      categoryName: cashflowViewConsts.CATEGORY_NAMES.TRACKING,
      expense: category.categoryName,
      cashflowCategory: cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING,
      icon: category.icon,
      label: category.categoryName,
      pluralLabel: category.categoryName,
      style: cashflowViewConsts.CATEGORY_STYLE[cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING],
      targetPopup: ChangeTransactionCategorySettingsPopup,
    };
  });
}

function getRelevantRecurringCategories(allCategories, availableCashflowCategories) {
  if (availableCashflowCategories.includes(cashflowViewConsts.CASHFLOW_CATEGORIES.RECURRING)) {
    const economyCategory = _.find(cashflowViewConsts.CATEGORIES, { categoryName: cashflowViewConsts.CATEGORY_NAMES.RECURRING_ECONOMY });
    return _.compact([economyCategory]);
  }
  return [];
}

function getTrackingCategories(allCategories, availableCashflowCategories) {
  if (availableCashflowCategories.includes(cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING)) {
    return _.filter(allCategories, category => category.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING);
  }
  return [];
}

function getNonRecurringCategories(categories, availableCashflowCategories) {
  const nonRecurringCashflowCategories = _.difference(
    availableCashflowCategories,
    [cashflowViewConsts.CASHFLOW_CATEGORIES.RECURRING, cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING],
  );
  return _.filter(categories, category => _.includes(nonRecurringCashflowCategories, category.cashflowCategory));
}

function orderCategories(categories) {
  const precedence = [
    cashflowViewConsts.CASHFLOW_CATEGORIES.VARIABLE,
    cashflowViewConsts.CASHFLOW_CATEGORIES.RECURRING,
    cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING,
    cashflowViewConsts.CASHFLOW_CATEGORIES.FIXED,
    cashflowViewConsts.CASHFLOW_CATEGORIES.EXCLUDED_EXPENSE,
    cashflowViewConsts.CASHFLOW_CATEGORIES.VARIABLE_INCOME,
    cashflowViewConsts.CASHFLOW_CATEGORIES.FIXED_INCOME,
    cashflowViewConsts.CASHFLOW_CATEGORIES.EXCLUDED_INCOME,
  ];
  return _.sortBy(categories, c => _.indexOf(precedence, c.cashflowCategory));
}

const state = {
  categoryRuleType: null,
  trackingCategories: null,
  transaction: null,
  originalCategoryName: null,
  originalCategoryId: null,
  selectedCategoryName: null,
  selectedCategoryId: null,
};

const getters = {
  ENRICHMENT_INPUT_TYPES: () => ENRICHMENT_INPUT_TYPES,
  allCategories: state => [...cashflowViewConsts.CATEGORIES, ...normalizeTrackingCategories(state.trackingCategories)],
  categoryToVariant: () => cashflowViewConsts.CATEGORY_STYLE,
  isIncomeCategory: state => _.includes(
    [
      cashflowViewConsts.CATEGORY_NAMES.VARIABLE_INCOME,
      cashflowViewConsts.CATEGORY_NAMES.FIXED_INCOME,
      cashflowViewConsts.CATEGORY_NAMES.EXCLUDED_INCOME,
    ],
    state.originalCategoryName,
  ),
  incomeOrExpense: (state, getters) => (getters.isIncomeCategory ? 'הכנסה' : 'הוצאה'),
  originalCategory: (state, getters) => {
    if (state.originalCategoryId) {
      return _.find(getters.allCategories, { categoryId: state.originalCategoryId });
    }
    return _.find(getters.allCategories, { categoryName: state.originalCategoryName });
  },
  selectedCategory: (state, getters) => {
    if (state.selectedCategoryId) {
      return _.find(getters.allCategories, { categoryId: state.selectedCategoryId });
    }
    return _.find(getters.allCategories, { categoryName: state.selectedCategoryName });
  },
  sameCategorySelected: state => {
    if (state.selectedCategoryId) {
      return state.selectedCategoryId === state.originalCategoryId;
    }
    return state.selectedCategoryName === state.originalCategoryName;
  },
  originalCashflowCategory: (state, getters) => getters.originalCategory.cashflowCategory,
  availableCashflowCategories: (state, getters) => cashflowViewConsts.AVAILABLE_CASHFLOW_CATEGORIES[getters.originalCashflowCategory],
  availableCategories: (state, getters) => {
    const nonRecurringCategories = getNonRecurringCategories(getters.allCategories, getters.availableCashflowCategories);
    const recurringCategories = getRelevantRecurringCategories(
      getters.allCategories,
      getters.availableCashflowCategories,
    );
    const trackingCategories = getTrackingCategories(getters.allCategories, getters.availableCashflowCategories);
    return orderCategories([...nonRecurringCategories, ...recurringCategories, ...trackingCategories]);
  },
  valueForPredicate: state => {
    if (state.categoryRuleType === ENRICHMENT_INPUT_TYPES.BUSINESS_NAME) {
      return state.transaction.businessName;
    }
    if (state.categoryRuleType === ENRICHMENT_INPUT_TYPES.TRANSACTION_ID) {
      return state.transaction.transactionId;
    }
    return null;
  },
};

const actions = {
  startEditingTransaction({ commit, rootGetters, dispatch }, { transaction, categoryName, categoryId }) {
    dispatch('reset');
    commit('setTransaction', transaction);
    if (categoryName && !categoryId) {
      commit('setOriginalCategoryName', categoryName);
      commit('setSelectedCategoryName', categoryName);
    } else if (categoryId) {
      commit('setOriginalCategoryId', categoryId);
      commit('setSelectedCategoryId', categoryId);
    }
    commit('setTrackingCategories', rootGetters['cashflowView/trackingCategories']);
  },
  reset({ commit }) {
    commit('setOriginalCategoryName', null);
    commit('setSelectedCategoryName', null);
    commit('setOriginalCategoryId', null);
    commit('setSelectedCategoryId', null);
  },
};

const mutations = {
  setSelectedCategoryName(state, selectedCategoryName) {
    state.selectedCategoryName = selectedCategoryName;
    state.selectedCategoryId = null;
  },
  setSelectedCategoryId(state, selectedCategoryId) {
    state.selectedCategoryId = selectedCategoryId;
    state.selectedCategoryName = null;
  },
  setTrackingCategories(state, trackingCategories) {
    state.trackingCategories = trackingCategories;
  },
  setTransaction(state, transaction) {
    state.transaction = transaction;
  },
  setCategoryRuleType(state, categoryRuleType) {
    state.categoryRuleType = categoryRuleType;
  },
  setOriginalCategoryName(state, originalCategoryName) {
    state.originalCategoryName = originalCategoryName;
  },
  setOriginalCategoryId(state, originalCategoryId) {
    state.originalCategoryId = originalCategoryId;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
